import { useAdminUserList } from "@/api/services/admin/user";
import { TGetUserListDto } from "@/api/services/admin/user/dtos/get-user-list.dto";
import { useEnum } from "@/api/services/global/enum";
import { IconBan, IconOutlineInfo } from "@/assets/icons";
import { NewPagination } from "@/components/elements/Pagination/NewPagination";
import { Table, TableSorting } from "@/components/elements/Table";
import { DATE_FORMAT } from "@/constants/datetime";
import { USER_ACTIVE_STATUS } from "@/constants/enum";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { findLabelByValue } from "@/utils/object";
import { truncateWithEllipsis } from "@/utils/string";
import { button } from "@/theme/colors";
import { getDynamicRoute } from "@/utils/url";
import { Box, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// Color for inactive rows and icons
const INACTIVE_COLOR = "#CACDCD";

export const AdminUserTable = () => {
  const navigate = useNavigate();
  const { data: adminRole } = useEnum("EAdminRole");
  const columns = useMemo<MRT_ColumnDef<TGetUserListDto>[]>(
    () => [
      {
        header: "状態",
        id: "status",
        size: 60,
        muiTableHeadCellProps: {
          align: "center",
        },
        muiTableBodyCellProps: ({ row }) => ({
          align: "center",
          sx: {
            color: row.original.activeStatus === USER_ACTIVE_STATUS.INACTIVE ? INACTIVE_COLOR : "inherit",
          },
        }),
        Cell: ({ row }) => {
          const { activeStatus, lastLoginTime } = row.original;
          // Show ban icon for inactive users
          if (activeStatus === USER_ACTIVE_STATUS.INACTIVE) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", height: 28 }}>
                <IconBan style={{ fontSize: 20, color: button.lightDisable }} />
              </Box>
            );
          }
          // Show info icon for active users who haven't logged in yet (pending)
          if (!lastLoginTime) {
            return (
              <Tooltip title="招待送付済みです。パスワード登録およびログイン後にこのマークは消えます。" arrow placement="right">
                <Box sx={{ display: "flex", alignItems: "center", height: 28, cursor: "pointer" }}>
                  <IconOutlineInfo style={{ fontSize: 20, color: INACTIVE_COLOR }} />
                </Box>
              </Tooltip>
            );
          }
          // No icon for normal active users
          return null;
        },
      },
      {
        header: "担当者名",
        id: "fullName",
        size: 353,
        Header: <TableSorting name="user.fullName">担当者名</TableSorting>,
        muiTableBodyCellProps: ({ row }) => ({
          sx: {
            color: row.original.activeStatus === USER_ACTIVE_STATUS.INACTIVE ? INACTIVE_COLOR : "inherit",
          },
        }),
        Cell: ({ row }) => {
          const text = row.original.fullName || EMPTY_STR.TEXT;
          return <Box title={text}>{truncateWithEllipsis(text, 50)}</Box>;
        },
      },
      {
        header: "メールアドレス",
        id: "email",
        size: 353,
        Header: <TableSorting name="user.email">メールアドレス</TableSorting>,
        muiTableBodyCellProps: ({ row }) => ({
          sx: {
            color: row.original.activeStatus === USER_ACTIVE_STATUS.INACTIVE ? INACTIVE_COLOR : "inherit",
          },
        }),
        Cell: ({ row }) => row.original.email || EMPTY_STR.TEXT,
      },
      {
        header: "担当",
        id: "adminRole",
        size: 200,
        Header: <TableSorting name="user.adminRole">担当</TableSorting>,
        muiTableBodyCellProps: ({ row }) => ({
          sx: {
            color: row.original.activeStatus === USER_ACTIVE_STATUS.INACTIVE ? INACTIVE_COLOR : "inherit",
          },
        }),
        Cell: ({ row }) => findLabelByValue(adminRole, row.original.adminRole) || EMPTY_STR.TEXT,
      },
      {
        header: "登録日",
        id: "createdAt",
        size: 120,
        Header: <TableSorting name="user.createdAt">登録日</TableSorting>,
        muiTableBodyCellProps: ({ row }) => ({
          sx: {
            color: row.original.activeStatus === USER_ACTIVE_STATUS.INACTIVE ? INACTIVE_COLOR : "inherit",
          },
        }),
        Cell: ({ row }) => {
          const date = dayjs(row.original.createdAt);
          return date.isValid() ? date.format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
      },
    ],
    [adminRole],
  );

  const [heightTable] = useState<string>("calc(100vh - 290px)");

  const { data, isLoading } = useAdminUserList();
  const { table } = useTable({
    columns,
    rows: data?.data ?? [],
    onClickRow: (row) => navigate(getDynamicRoute(AppRoutes.adminUserDetail, { id: row.id })),
    renderEmptyRowsFallback: () => <span></span>,
  });

  return (
    <>
      <Table table={table} isLoading={isLoading} height={heightTable} minHeight={150} />
      <NewPagination totalPage={data?.pagination.totalPage} />
    </>
  );
};
